/**
 * RoomSummary (PPL-35) — read-only view of the caller's exit summary.
 *
 * Reads `mediations/{id}/exitSummary/{uid}` via a real-time onSnapshot
 * (written by endMediationKeepSummaryEurW). Shows a loading state while the
 * server generates the summary; renders the text once available.
 *
 * The summary is the f8-authored paragraph for the caller's side only —
 * what was brought, what shifted. The other side has their own version.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getMediationCounterpart, subscribeToExitSummary, subscribeToRoom } from '../services/roomService';
import type { Room, RoomExitSummary } from '../types/room';
import RoomBlockHatch from '../components/RoomBlockHatch';
import type { TimestampLike, FirebaseTimestamp } from '../types/pplTalk';
import '../styles/RoomSummary.css';

// ─── Timestamp helper ─────────────────────────────────────────────────────────

function fmtDate(value: TimestampLike | null): string {
  if (!value) return '';
  let d: Date;
  if (value instanceof Date) {
    d = value;
  } else if (typeof value === 'number') {
    d = new Date(value);
  } else if (typeof value === 'string') {
    d = new Date(value);
  } else {
    const ts = value as FirebaseTimestamp;
    d = typeof ts.toDate === 'function' ? ts.toDate!() : new Date(ts.seconds * 1000);
  }
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const RoomSummary: React.FC = () => {
  const { mediationId } = useParams<{ mediationId: string }>();
  const navigate = useNavigate();

  const uid = getAuth().currentUser?.uid ?? null;

  const [summary, setSummary] = useState<RoomExitSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track the room state too, so an ended room with no summary reads honestly
  // ("no summary was kept") rather than "being written…" forever — there is no
  // async generation: a summary only exists if the end-flow created one.
  const [roomEnded, setRoomEnded] = useState(false);
  const [room, setRoom] = useState<Room | null>(null);
  const [otherName, setOtherName] = useState('them');

  useEffect(() => {
    if (!mediationId) return undefined;
    const unsub = subscribeToRoom(
      mediationId,
      (r) => {
        setRoom(r);
        setRoomEnded(r?.state === 'ended');
      },
      () => { /* non-fatal — summary subscription drives the main states */ },
    );
    return () => unsub();
  }, [mediationId]);

  // Counterpart first name for the block hatch (personal register).
  useEffect(() => {
    if (!mediationId) return;
    void getMediationCounterpart(mediationId)
      .then(({ counterpart }) => {
        if (counterpart.firstName) setOtherName(counterpart.firstName.toLowerCase());
      })
      .catch(() => { /* fallback 'them' */ });
  }, [mediationId]);

  useEffect(() => {
    if (!uid || !mediationId) {
      setLoading(false);
      setError('Unable to load summary.');
      return;
    }
    setLoading(true);
    setError(null);

    const unsub = subscribeToExitSummary(
      mediationId,
      uid,
      (data) => {
        setSummary(data);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load summary.');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [mediationId, uid]);

  return (
    <div className="med-summary-page">
      {/* Top nav — no back button (forward-only rooms flow, PPL-161): this is
          a terminal sealed page; history-back walked into the end chooser of
          an already-ended room. The rail handles navigation. */}
      <div className="med-summary-topnav">
        <div style={{ width: 34 }} />
        <span className="med-summary-eyebrow">your summary · f8</span>
        <div style={{ width: 34 }} />
      </div>

      <div className="med-summary-content">
        {loading ? (
          <div className="med-summary-state">
            <div className="med-summary-spinner" aria-label="loading" />
            <p className="med-summary-state-text">preparing your summary…</p>
          </div>
        ) : error ? (
          <div className="med-summary-state">
            <p className="med-summary-state-text med-summary-error">{error}</p>
          </div>
        ) : !summary ? (
          <div className="med-summary-state">
            <p className="med-summary-state-text">
              {roomEnded
                ? 'no summary was kept for this room.'
                : 'your summary is being written — it will appear here shortly.'}
            </p>
          </div>
        ) : (
          <>
            <div className="med-summary-header">
              <h2 className="med-summary-title">what f8 heard from your side.</h2>
              {summary.createdAt && (
                <span className="med-summary-date">{fmtDate(summary.createdAt)}</span>
              )}
            </div>

            <div className="med-summary-card">
              <p className="med-summary-text">{summary.text}</p>
            </div>

            <p className="med-summary-footer">
              only you can see this. the other side has their own version.
            </p>
          </>
        )}

        {/* PPL-234 — explicit exits. The forward-only flow (PPL-161) removed
            history-back from this sealed page but left NO way out at all: the
            shell sidebar is hidden on mobile widths, so the page was a dead
            end — including for the participant ejected here when the other
            side ends the room. Rendered in every state (loaded, empty, error):
            an exit must never depend on the summary loading. */}
        {!loading && (
          <div className="med-summary-actions">
            <button
              type="button"
              className="med-summary-home-btn"
              onClick={() => navigate('/f8chat')}
            >
              back to home
            </button>
            <button
              type="button"
              className="med-summary-rooms-btn"
              onClick={() => navigate('/rooms')}
            >
              your rooms
            </button>
          </div>
        )}

        {/* PPL-190 — quiet block exit hatch on the sealed surface. */}
        {roomEnded && uid && room && (
          <RoomBlockHatch
            otherUid={uid === room.inviterUid ? room.inviteeUid : room.inviterUid}
            otherName={otherName}
          />
        )}
      </div>
    </div>
  );
};

export default RoomSummary;
